import React, { useState, useMemo, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Tooltip,
  IconButton,
  Dialog,
  Typography,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import LinearProgress from '@mui/material/LinearProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { auth0Service } from '../../services/auth0.service';
import { ValidateEmail } from '../../services/emailvalidations';
import { getJwtToken } from '../../services/jwt.services';
import jwt_decode from 'jwt-decode';
import styles from './Auth0UserBlocks.module.css';

export default function Auth0UserBlocks() {
  const [inputEmail, setInputEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [auth0permissions, setAuth0permissions] = useState([]);

  useEffect(() => {
    const token = getJwtToken();
    if (token) {
      const { auth0permissions: perms } = jwt_decode(token);
      setAuth0permissions(perms || []);
    }
  }, []);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setInputEmail(value);
    if (value && !ValidateEmail(value)) {
      setEmailError('Please enter a valid email address.');
    } else {
      setEmailError('');
    }
  };

  const handleSearch = async () => {
    if (!ValidateEmail(inputEmail)) return;
    setLoading(true);
    setBlocks([]);
    try {
      const response = await auth0Service.getUserBlocks(inputEmail);
      const blockedFor = response?.data?.blocked_for ?? [];
      setBlocks(blockedFor);
      if (blockedFor.length === 0) {
        setSnackbar({ open: true, message: 'No blocks found for this email.', severity: 'info' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to fetch blocks. Please try again.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && ValidateEmail(inputEmail) && !emailError) {
      handleSearch();
    }
  };

  const handleReset = () => {
    setInputEmail('');
    setEmailError('');
    setBlocks([]);
  };

  const handleDeleteClick = (block) => {
    setSelectedBlock(block);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedBlock(null);
  };

  const handleConfirmRemove = async () => {
    if (!selectedBlock) return;
    setRemoving(true);
    try {
      await auth0Service.removeUserBlock(selectedBlock.identifier);
      setBlocks((prev) => prev.filter((b) => b !== selectedBlock));
      setSnackbar({ open: true, message: 'Block removed successfully.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to remove block. Please try again.', severity: 'error' });
    } finally {
      setRemoving(false);
      setOpenDialog(false);
      setSelectedBlock(null);
    }
  };

  const columns = useMemo(() => [
    { accessorKey: 'identifier', header: 'Identifier', size: 220 },
    { accessorKey: 'connection', header: 'Connection', size: 250 },
    { accessorKey: 'ip', header: 'IP Address', size: 160 },
  ], []);

  const isSearchDisabled = !inputEmail || Boolean(emailError);

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.headerRow}>
          <Button
            className={styles.backButton}
            onClick={() => window.history.back()}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          <Typography variant="h6" className={styles.tableHeader}>
            Auth0 Unblock User
          </Typography>
        </div>
      </div>

      <div className={styles.formContainer}>
        <div className={styles.formRow}>
          <TextField
            className={styles.inputField}
            label="Email Address"
            variant="outlined"
            value={inputEmail}
            onChange={handleEmailChange}
            onKeyPress={handleKeyPress}
            error={Boolean(emailError)}
            helperText={emailError}
            placeholder="user@example.com"
          />
          <Button
            variant="contained"
            className={styles.submitButton}
            onClick={handleSearch}
            disabled={isSearchDisabled || loading}
          >
            Search Blocks
          </Button>
          <Button
            variant="outlined"
            className={styles.resetButton}
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
          >
            Reset
          </Button>
        </div>
      </div>

      {loading && (
        <div className={styles.progressContainer}>
          <LinearProgress color="secondary" />
        </div>
      )}

      <Paper className={styles.tableContainer}>
        <div className={styles.tableWrapper}>
          <MaterialReactTable
            columns={columns}
            data={blocks}
            enablePagination={false}
            enableSorting={false}
            enableBottomToolbar={false}
            enableColumnFilters={false}
            enableRowActions={true}
            muiTableBodyRowProps={{ hover: false }}
            positionToolbarAlertBanner="bottom"
            renderEmptyRowsFallback={() => (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
                <Typography variant="body1" color="text.secondary">
                  {loading ? 'Loading...' : 'No blocks found. Enter an email and click "Search Blocks".'}
                </Typography>
              </Box>
            )}
            renderRowActions={({ row }) => (
              <Box sx={{ display: 'flex', gap: '8px' }}>
                <Tooltip arrow placement="right" title="Remove block">
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteClick(row.original)}
                    disabled={removing || !auth0permissions.includes('write')}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          />
        </div>
      </Paper>

      <Dialog open={openDialog} onClose={handleDialogClose} PaperProps={{ style: { borderRadius: '8px' } }}>
        <DialogTitle className={styles.dialogTitle}>Confirm Remove Block</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <DialogContentText>
            Remove block for <strong>{selectedBlock?.identifier}</strong> on connection{' '}
            <strong>{selectedBlock?.connection}</strong> (IP: {selectedBlock?.ip})?
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={handleDialogClose} className={styles.dialogCancelButton} disabled={removing}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRemove} className={styles.dialogDeleteButton} disabled={removing}>
            {removing ? 'Removing...' : 'Remove Block'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
